import { useInfiniteQuery, useQuery, type InfiniteData } from '@tanstack/react-query';
import {
  fetchScreener,
  fetchScreenerFacets,
  fetchScreenerStats,
  fetchCompany,
  type ScreenerQuery,
  type ScreenerListResponse,
  type ScreenerRow,
  type ScreenerFacets,
  type ScreenerStats,
  type CompanyDetailResponse,
} from './screener-client.js';

// The screener lazy-loads on scroll (the panel only fetches the next page when
// the virtualiser nears the end), so a moderate page keeps the first paint
// snappy and bounds the per-request work — every row is transferred, mapped,
// and JSON-serialised synchronously, so payload size (not the 15ms indexed
// query) is the real cost. 500 covers the initial viewport with a deep scroll
// buffer; more pages stream in as needed. Halved from 1000 after the €150k
// floor tripled the in-scope set. TanStack Virtual renders only what's on
// screen, so DOM cost stays flat regardless.
const PAGE_SIZE = 500;

// useInfiniteScreener — paginated list view. Mirrors the existing
// useInfiniteEntities pattern: TanStack useInfiniteQuery with cursor-as-
// offset, flattens pages for the virtualiser.
export function useInfiniteScreener(query: Omit<ScreenerQuery, 'offset' | 'limit'>) {
  return useInfiniteQuery<
    ScreenerListResponse,
    Error,
    InfiniteData<ScreenerListResponse>,
    readonly [string, Omit<ScreenerQuery, 'offset' | 'limit'>],
    number
  >({
    queryKey: ['screener', query] as const,
    queryFn: ({ pageParam }) => fetchScreener({ ...query, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const consumed = lastPage.offset + lastPage.rows.length;
      return consumed < lastPage.total ? consumed : undefined;
    },
    staleTime: 30_000, // 30 s — filter changes invalidate via the query key
  });
}

export function flattenPages(data: InfiniteData<ScreenerListResponse> | undefined): ScreenerRow[] {
  if (!data) return [];
  const out: ScreenerRow[] = [];
  for (const page of data.pages) for (const r of page.rows) out.push(r);
  return out;
}

export function totalFromPages(data: InfiniteData<ScreenerListResponse> | undefined): number {
  return data?.pages[0]?.total ?? 0;
}

export function useScreenerFacets(level?: 'legacy' | 'nuts1' | 'nuts2' | 'nuts3') {
  return useQuery<ScreenerFacets, Error>({
    queryKey: ['screener-facets', level ?? 'nuts2'] as const,
    queryFn: () => fetchScreenerFacets(level),
    staleTime: 5 * 60_000, // facets change rarely
  });
}

export function useScreenerStats() {
  return useQuery<ScreenerStats, Error>({
    queryKey: ['screener-stats'] as const,
    queryFn: fetchScreenerStats,
    staleTime: 5 * 60_000,
  });
}

export function useCompany(entityId: string | null) {
  return useQuery<CompanyDetailResponse, Error>({
    queryKey: ['company', entityId] as const,
    queryFn: () => fetchCompany(entityId as string),
    enabled: !!entityId,
    staleTime: 30_000,
  });
}
